import React, { useState } from 'react';
import {
    Button,
    Typography,
    Stack,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Divider,
    Avatar,
    IconButton,
    Tooltip,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    Checkbox,
    CircularProgress,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { ArrowBack, Info } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import { APP_CONFIG } from '../../../../../config/app.config';
import { InventoryPreview } from '@features/warehouses/components/inventory-preview/InventoryPreview';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import BolCredentialsForm from '@features/connections/bol/components/BolCredentialsForm';
import WarehouseConnectionSelect from '../WarehouseConnectionSelect';
import { useWarehouses } from '../../../hooks/useWarehouseOperations';
import { WarehouseConnection } from '@domain/inventory/models/WarehouseConnection';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { BolEndpoint } from '@domain/connection/enums/endpoints.enum';
import { useImportJobsStore } from '../../../../../application/store/importJobsStore';

interface BolWarehouseFormProps {
    onClose: () => void;
    onBack: () => void;
}

const BolWarehouseForm: React.FC<BolWarehouseFormProps> = ({
    onClose,
    onBack,
}) => {
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { createWarehouse, isCreating } = useWarehouses();
    const addJob = useImportJobsStore((s) => s.addJob);
    const [form, setForm] = useState({
        name: '',
        location: '',
    });

    const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
    const [selectedSyncType, setSelectedSyncType] = useState<'offer' | 'lvb' | null>(null);
    const [showCreateConnection, setShowCreateConnection] = useState(false);
    const [itemsToShow, setItemsToShow] = useState(4);
    const [onlyManagedByRetailer, setOnlyManagedByRetailer] = useState(true);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const { data: inventoryPreview, isLoading: previewIsLoading } = useQuery({
        queryKey: ['bol-inventory-preview', selectedConnection?.id, selectedSyncType],
        queryFn: async () => {

            if (!selectedConnection || !selectedSyncType) return null;

            const result = await container.bolConnectorRepository.fetchInventoryPreview(selectedConnection.id, selectedSyncType);
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        enabled: !!selectedConnection && !!selectedSyncType
    })

    // Filter inventory preview based on managedByRetailer setting
    const filteredInventoryPreview = inventoryPreview?.filter(item => {
        if (selectedSyncType === 'offer' && onlyManagedByRetailer) {
            return item.filters?.stockManagedByRetailer === 'true' || item.filters?.stockManagedByRetailer === true;
        }
        return true;
    });

    const selectSyncType = (type: 'offer' | 'lvb') => {
        setItemsToShow(4);

        const syncTypeLabel = type === 'offer' ? 'Own inventory' : 'LVB';
        setForm({ name: `Bol ${syncTypeLabel}`, location: '' });
        setSelectedSyncType(type);
    }

    const handleClose = () => {
        setForm({ name: '', location: '' });
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedConnection || !selectedSyncType) return;

        const warehouse: {
            name: string;
            location: string;
            warehouseConnections: Omit<WarehouseConnection, 'id'>[];
        } = {
            name: form.name,
            location: form.location,
            warehouseConnections: [
                {
                    sourceType: WarehouseConnectionType.BOL,
                    mutationType: MutationType.SYNC,
                    rules: [
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.CONNECTION_ID,
                            value: selectedConnection?.id
                        },
                        {
                            operator: RuleOperator.EQUALS,
                            field: RuleField.TYPE,
                            value: selectedSyncType
                        },
                    ],
                }
            ]
        }

        if (selectedSyncType === 'offer' && onlyManagedByRetailer) {
            warehouse.warehouseConnections[0].rules.push({
                operator: RuleOperator.EQUALS,
                field: RuleField.STOCK_MANAGED_BY_RETAILER,
                value: true
            })
        }

        await createWarehouse(warehouse);

        const syncEndpoint = selectedSyncType === 'offer' ? BolEndpoint.OFFERS : BolEndpoint.INVENTORY;
        const syncResult = await container.bolConnectorRepository.syncBolMarketplaceConnection(selectedConnection.id, syncEndpoint);
        if (syncResult.status === 'success') {
            addJob({ id: syncResult.data.jobId, connector: 'bol', label: `${selectedConnection.name} · ${form.name}` });
        }

        handleClose();
    };

    const handleCreateConnection = () => {
        setShowCreateConnection(true);
    };

    const handleNewConnection = (marketplaceConnection: any) => {
        setShowCreateConnection(false);
        queryClient.invalidateQueries({ queryKey: ['connections'] });
        setSelectedConnection(marketplaceConnection);
    };

    const handleCloseCreateConnection = () => {
        setShowCreateConnection(false);
    };


    const { data: connector } = useQuery({
        queryKey: ['bol-connector'],
        queryFn: async () => {
            const result = await container.marketplaceRepository.fetchConnectors();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data.find(connector => connector.type === 'bol');
        }
    })

    return (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%' }}>
            <DialogHeader>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center">
                        <IconButton onClick={onBack} color="inherit">
                            <ArrowBack />
                        </IconButton>

                        <Typography variant="h6" component="div">
                            Sync with Bol
                        </Typography>
                    </Stack>
                </Stack>
            </DialogHeader>

            <DialogContent>
                <Stack gap={3} sx={{ mt: 2 }}>
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Box sx={{ width: 100, height: 60, borderRadius: 1, border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
                                <img src={connector?.image} alt="Bol" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" color="text.primary">
                                    Sync your inventory with Bol
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Your Bol inventory wil be synced with your Inventory every hour.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />


                    <WarehouseConnectionSelect
                        connectorType={ClientType.BOL}
                        selectedConnection={selectedConnection}
                        onSelect={setSelectedConnection}
                        onCreateConnection={handleCreateConnection}
                        connectorName="Bol"
                    />



                    {selectedConnection && (
                        <Stack gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                What would you like to sync?
                            </Typography>


                            <Stack gap={2} direction="row">
                                <Button
                                    key='lvb'
                                    variant="outlined"
                                    onClick={() => selectSyncType('lvb')}
                                    sx={{
                                        borderColor: selectedSyncType === 'lvb' ? 'primary.main' : 'divider',
                                        borderWidth: 1,
                                        borderRadius: 1,
                                        padding: 2,
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.primary">
                                        LVB
                                    </Typography>
                                </Button>

                                <Button
                                    key='offer'
                                    variant="outlined"
                                    onClick={() => selectSyncType('offer')}
                                    sx={{
                                        borderColor: selectedSyncType === 'offer' ? 'primary.main' : 'divider',
                                        borderWidth: 1,
                                        borderRadius: 1,
                                        padding: 2,
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="subtitle2" color="text.primary">
                                            Own inventory
                                        </Typography>
                                        <Tooltip title="This option requires you to have your own inventory synced to your Bol account">
                                            <Info sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        </Tooltip>
                                    </Stack>
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    {selectedConnection && selectedSyncType === 'offer' && (
                        <Alert
                            severity="warning"

                        >
                            <Stack spacing={1}>
                                <Typography variant="body2">
                                    Bol only supports 999 items per product. If you have more inventory it will show up as 999
                                </Typography>

                            </Stack>
                        </Alert>
                    )}

                    {selectedConnection && selectedSyncType && (
                        <>
                            <Stack gap={2} direction="column">
                                <Typography variant="body2" color="text.secondary">
                                    Warehouse details
                                </Typography>
                                
                                <Stack gap={2} direction="row">
                                    <TextField
                                        label="Warehouse name"
                                        value={form.name}
                                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                        fullWidth
                                        size="small"
                                    />
                                    
                                    <TextField
                                        label="Location"
                                        value={form.location}
                                        onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                                        fullWidth
                                        size="small"
                                        placeholder="e.g., Amsterdam, Netherlands"
                                    />
                                </Stack>
                            </Stack>

                            <InventoryPreview
                                inventory={filteredInventoryPreview}
                                isLoading={previewIsLoading}
                                emptyMessage="No inventory to show for this connection"
                            />
                        </>
                    )}

                    <Stack gap={2}>
                        {showAdvanced && (
                            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                <Stack gap={2}>
                                    <Typography variant="subtitle2" color="text.primary">
                                        Sync options
                                    </Typography>

                                    {selectedSyncType === 'offer' ? (
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                id="onlyManagedByRetailer"
                                                checked={onlyManagedByRetailer}
                                                onChange={(e) => setOnlyManagedByRetailer(e.target.checked)}
                                            />
                                            <label htmlFor="onlyManagedByRetailer">
                                                <Typography variant="body2" color="text.primary">
                                                    Only sync items managed by retailer
                                                </Typography>
                                            </label>
                                        </Stack>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            No advanced settings available for this sync type
                                        </Typography>
                                    )}

                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ position: 'sticky', bottom: 0, backgroundColor: 'background.paper', borderTop: 1, p: 3, borderColor: 'divider', justifyContent: 'space-between' }}>

                <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    sx={{ minWidth: 'auto' }}
                >
                    {showAdvanced ? 'Hide' : 'Show'} advanced
                </Button>

                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!selectedConnection || !selectedSyncType}
                >
                    Create warehouse
                </Button>
            </DialogActions>

            {/* Create Bol Connection Dialog */}
            <Dialog
                open={showCreateConnection}
                onClose={handleCloseCreateConnection}
                maxWidth="sm"
                fullWidth
            >
                <BolCredentialsForm
                    onNewConnection={handleNewConnection}
                    onClose={handleCloseCreateConnection}
                    searchParams={searchParams.toString()}
                />
            </Dialog>
        </Box>

    );
};

export default BolWarehouseForm; 